import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, createApiClient } from '../App';
import { toast } from 'sonner';
import {
  Plus,
  Link as LinkIcon,
  SpinnerGap,
  MagnifyingGlass,
  Warning,
  CheckCircle,
  ArrowSquareOut
} from '@phosphor-icons/react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const AddRestaurantModal = ({ open, onOpenChange, onSuccess }) => {
  const { token } = useAuth();
  const api = createApiClient(token);

  const [mode, setMode] = useState('quick'); // quick | url | manual
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [heroImageUrl, setHeroImageUrl] = useState('');

  // Duplicate detection state
  const [dupeResult, setDupeResult] = useState(null);
  const [checkingDupe, setCheckingDupe] = useState(false);
  const [dupeOverride, setDupeOverride] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [suburb, setSuburb] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [description, setDescription] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');

  const resetForm = () => {
    setName('');
    setUrl('');
    setSuburb('');
    setCuisine('');
    setPriceRange('');
    setDescription('');
    setBookingLink('');
    setWebsiteUrl('');
    setInstagramUrl('');
    setHeroImageUrl('');
    setMode('quick');
    setDupeResult(null);
    setDupeOverride(false);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // Debounced duplicate check as user types
  useEffect(() => {
    if (!name || name.trim().length < 3) {
      setDupeResult(null);
      return;
    }
    setDupeOverride(false);
    const timer = setTimeout(async () => {
      setCheckingDupe(true);
      try {
        const res = await api.post('/restaurants/check-duplicate', {
          name: name.trim(),
          suburb: suburb.trim() || null
        });
        setDupeResult(res.data);
      } catch {
        setDupeResult(null);
      } finally {
        setCheckingDupe(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [name, suburb]);

  const handleQuickAdd = async () => {
    if (!name.trim()) {
      toast.error('Please enter a restaurant name');
      return;
    }

    if (dupeResult?.has_duplicates && !dupeOverride) {
      toast.warning('Duplicate detected! Click "Add Anyway" to proceed.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/restaurants', {
        name: name.trim(),
        suburb: suburb.trim() || null,
        cuisine: cuisine ? cuisine.split(',').map(c => c.trim()).filter(Boolean) : [],
        price_range: priceRange || null,
        state: 'want_to_try'
      });
      
      onSuccess(response.data);
      handleClose();
    } catch (error) {
      console.error('Error adding restaurant:', error);
      toast.error('Failed to add restaurant');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlEnrich = async () => {
    if (!url.trim()) {
      toast.error('Please enter a URL');
      return;
    }

    setEnriching(true);
    try {
      const response = await api.post('/restaurants/enrich', {
        url: url.trim()
      });

      const data = response.data;
      
      if (data.name) setName(data.name);
      if (data.suburb) setSuburb(data.suburb);
      if (data.cuisine) setCuisine(Array.isArray(data.cuisine) ? data.cuisine.join(', ') : data.cuisine);
      if (data.price_range) setPriceRange(data.price_range);
      if (data.description) setDescription(data.description);
      if (data.booking_link) setBookingLink(data.booking_link);
      if (data.website_url) setWebsiteUrl(data.website_url);
      if (data.instagram_url) setInstagramUrl(data.instagram_url);
      if (data.hero_image_url) setHeroImageUrl(data.hero_image_url);

      setMode('manual');
      toast.success('Restaurant details extracted!');
    } catch (error) {
      console.error('Enrichment error:', error);
      toast.error('Failed to extract from URL. Try manual entry.');
      setMode('manual');
    } finally {
      setEnriching(false);
    }
  };

  const handleManualAdd = async () => {
    if (!name.trim()) {
      toast.error('Please enter a restaurant name');
      return;
    }

    if (dupeResult?.has_duplicates && !dupeOverride) {
      toast.warning('Duplicate detected! Click "Add Anyway" to proceed.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/restaurants', {
        name: name.trim(),
        suburb: suburb.trim() || null,
        cuisine: cuisine ? cuisine.split(',').map(c => c.trim()).filter(Boolean) : [],
        price_range: priceRange || null,
        description: description.trim() || null,
        booking_link: bookingLink.trim() || null,
        website_url: websiteUrl.trim() || null,
        instagram_url: instagramUrl.trim() || null,
        hero_image_url: heroImageUrl.trim() || null,
        source_url: url.trim() || null,
        state: 'want_to_try'
      });
      
      onSuccess(response.data);
      handleClose();
    } catch (error) {
      console.error('Error adding restaurant:', error);
      toast.error('Failed to add restaurant');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="add-restaurant-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Add Restaurant</DialogTitle>
        </DialogHeader>

        {/* Mode tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setMode('quick')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
              mode === 'quick' 
                ? 'bg-[#A94438] text-white' 
                : 'bg-[#E8DCD0]/50 text-[#7A7873] hover:bg-[#E8DCD0]'
            }`}
            data-testid="mode-quick"
          >
            Quick Add
          </button>
          <button
            onClick={() => setMode('url')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
              mode === 'url' 
                ? 'bg-[#A94438] text-white' 
                : 'bg-[#E8DCD0]/50 text-[#7A7873] hover:bg-[#E8DCD0]'
            }`}
            data-testid="mode-url"
          >
            From URL
          </button>
          <button
            onClick={() => setMode('manual')}
            className={`flex-1 py-2 px-4 rounded-full text-sm font-medium transition-colors ${
              mode === 'manual' 
                ? 'bg-[#A94438] text-white' 
                : 'bg-[#E8DCD0]/50 text-[#7A7873] hover:bg-[#E8DCD0]'
            }`}
            data-testid="mode-manual"
          >
            Manual
          </button>
        </div>

        {/* Quick Add Mode */}
        {mode === 'quick' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="quick-name">Restaurant Name *</Label>
              <Input
                id="quick-name"
                placeholder="e.g., The Test Kitchen"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
                data-testid="quick-name-input"
              />
            </div>
            <div>
              <Label htmlFor="quick-suburb">Suburb</Label>
              <Input
                id="quick-suburb"
                placeholder="e.g., Woodstock"
                value={suburb}
                onChange={(e) => setSuburb(e.target.value)}
                className="mt-1.5"
                data-testid="quick-suburb-input"
              />
            </div>
            <DuplicateWarning
              result={dupeResult}
              checking={checkingDupe}
              overridden={dupeOverride}
              onOverride={() => setDupeOverride(true)}
            />
            <Button 
              onClick={handleQuickAdd} 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="quick-add-btn"
            >
              {loading ? (
                <SpinnerGap className="w-5 h-5 animate-spin" />
              ) : dupeResult?.has_duplicates && !dupeOverride ? (
                <>
                  <Warning weight="bold" className="w-5 h-5 mr-2" />
                  Duplicate Found — Review Below
                </>
              ) : (
                <>
                  <Plus weight="bold" className="w-5 h-5 mr-2" />
                  Add to Want to Try
                </>
              )}
            </Button>
          </div>
        )}

        {/* URL Enrich Mode */}
        {mode === 'url' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="url-input">Restaurant URL</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="url-input"
                  placeholder="Paste restaurant URL..."
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="flex-1"
                  data-testid="url-input"
                />
                <Button 
                  onClick={handleUrlEnrich}
                  disabled={enriching}
                  className="btn-secondary"
                  data-testid="enrich-btn"
                >
                  {enriching ? (
                    <SpinnerGap className="w-5 h-5 animate-spin" />
                  ) : (
                    <LinkIcon weight="bold" className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-[#7A7873] mt-2">
                Paste a URL from Eat Out, DinePlan, or any restaurant page
              </p>
            </div>
          </div>
        )}

        {/* Manual Mode */}
        {mode === 'manual' && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label htmlFor="manual-name">Restaurant Name *</Label>
              <Input
                id="manual-name"
                placeholder="e.g., The Test Kitchen"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
                data-testid="manual-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="manual-suburb">Suburb</Label>
                <Input
                  id="manual-suburb"
                  placeholder="e.g., Woodstock"
                  value={suburb}
                  onChange={(e) => setSuburb(e.target.value)}
                  className="mt-1.5"
                  data-testid="manual-suburb-input"
                />
              </div>
              <div>
                <Label htmlFor="manual-price">Price Range</Label>
                <Select value={priceRange} onValueChange={setPriceRange}>
                  <SelectTrigger className="mt-1.5" data-testid="price-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="budget">Budget (R)</SelectItem>
                    <SelectItem value="mid">Mid-range (RR)</SelectItem>
                    <SelectItem value="premium">Premium (RRR)</SelectItem>
                    <SelectItem value="luxury">Luxury (RRRR)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="manual-cuisine">Cuisine (comma separated)</Label>
              <Input
                id="manual-cuisine"
                placeholder="e.g., Italian, Mediterranean"
                value={cuisine}
                onChange={(e) => setCuisine(e.target.value)}
                className="mt-1.5"
                data-testid="manual-cuisine-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-desc">Description</Label>
              <Textarea
                id="manual-desc"
                placeholder="Brief description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1.5"
                rows={2}
                data-testid="manual-desc-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-booking">Booking Link</Label>
              <Input
                id="manual-booking"
                placeholder="https://..."
                value={bookingLink}
                onChange={(e) => setBookingLink(e.target.value)}
                className="mt-1.5"
                data-testid="manual-booking-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-website">Website</Label>
              <Input
                id="manual-website"
                placeholder="https://..."
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                className="mt-1.5"
                data-testid="manual-website-input"
              />
            </div>
            <div>
              <Label htmlFor="manual-instagram">Instagram</Label>
              <Input
                id="manual-instagram"
                placeholder="https://instagram.com/..."
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                className="mt-1.5"
                data-testid="manual-instagram-input"
              />
            </div>
            {heroImageUrl && (
              <div className="rounded-lg overflow-hidden border border-[#EAE8E3]">
                <img src={heroImageUrl} alt="Restaurant" className="w-full h-32 object-cover" />
                <p className="text-xs text-[#9A9590] px-2 py-1">Image from URL</p>
              </div>
            )}
            <DuplicateWarning
              result={dupeResult}
              checking={checkingDupe}
              overridden={dupeOverride}
              onOverride={() => setDupeOverride(true)}
            />
            <Button 
              onClick={handleManualAdd} 
              className="w-full btn-primary"
              disabled={loading}
              data-testid="manual-add-btn"
            >
              {loading ? (
                <SpinnerGap className="w-5 h-5 animate-spin" />
              ) : dupeResult?.has_duplicates && !dupeOverride ? (
                <>
                  <Warning weight="bold" className="w-5 h-5 mr-2" />
                  Duplicate Found — Review Below
                </>
              ) : (
                <>
                  <Plus weight="bold" className="w-5 h-5 mr-2" />
                  Add Restaurant
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============= Duplicate Warning Component =============

const DuplicateWarning = ({ result, checking, overridden, onOverride }) => {
  if (checking) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#9A9590] py-2" data-testid="dupe-checking">
        <SpinnerGap className="w-4 h-4 animate-spin" />
        <span>Checking for duplicates...</span>
      </div>
    );
  }

  if (!result || !result.has_duplicates) {
    if (result && result.discovery_suggestions?.length > 0) {
      return (
        <div className="bg-[#F0EDE8] rounded-lg p-3 border border-[#E8DCD0]" data-testid="dupe-discovery-suggestions">
          <div className="flex items-center gap-2 text-sm text-[#4B5945] mb-2">
            <CheckCircle weight="fill" className="w-4 h-4" />
            <span className="font-medium">Not in your list. Similar in Discovery:</span>
          </div>
          {result.discovery_suggestions.map(d => (
            <div key={d.id} className="flex items-center gap-2 text-sm text-[#5A5752] ml-6">
              <span>{d.name}</span>
              {d.suburb && <span className="text-xs text-[#9A9590]">({d.suburb})</span>}
              <span className="text-xs bg-[#E8DCD0] px-1.5 py-0.5 rounded">{d.source_name}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  }

  if (overridden) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#4B5945] py-1" data-testid="dupe-overridden">
        <CheckCircle weight="fill" className="w-4 h-4" />
        <span>Adding despite similar match. Proceed.</span>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF4F0] rounded-lg p-3 border border-[#A94438]/20" data-testid="dupe-warning">
      <div className="flex items-center gap-2 text-sm text-[#A94438] mb-2">
        <Warning weight="fill" className="w-4 h-4" />
        <span className="font-medium">
          {result.match_type === 'exact' ? 'Exact match' : 'Similar restaurant'} already in your list
        </span>
      </div>
      {result.matches?.map(m => (
        <div key={m.id} className="flex items-center justify-between text-sm text-[#5A5752] ml-6 mb-1">
          <span>
            {m.name}
            {m.suburb && <span className="text-xs text-[#9A9590] ml-1">({m.suburb})</span>}
            {m.similarity && <span className="text-xs text-[#A94438] ml-1">{m.similarity}% match</span>}
          </span>
          <span className="text-xs bg-[#E8DCD0] px-1.5 py-0.5 rounded capitalize">{m.state?.replace(/_/g, ' ')}</span>
        </div>
      ))}
      <button
        onClick={onOverride}
        className="mt-2 ml-6 text-sm text-[#A94438] underline hover:no-underline"
        data-testid="dupe-add-anyway-btn"
      >
        Add anyway — it's a different restaurant
      </button>
    </div>
  );
};

export default AddRestaurantModal;
